import React, { useRef, useState } from 'react';
import { teacherService, TeacherImportResult } from '../services/teacherService';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  schoolId: number;
}

const TeacherImportDialog: React.FC<Props> = ({ open, onClose, onSuccess, schoolId }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<TeacherImportResult | null>(null);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  const handleClose = () => {
    setFile(null);
    setImporting(false);
    setResult(null);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('请选择 Excel 文件');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const r = await teacherService.importTeachers(schoolId, file);
      setResult(r);
      if (r.success > 0) {
        // 调用方刷新列表
        onSuccess();
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between">
          <div>
            <h2 className="text-xl font-headline text-on-surface">批量导入老师</h2>
            <p className="text-xs text-on-surface-variant mt-0.5">
              Excel 表头：身份证号 / 姓名 / 工号 / 部门 / 职位 / 电话 / 校管理员 / 班级
            </p>
          </div>
          <button onClick={handleClose} className="text-on-surface-variant hover:text-on-surface text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!result && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">表格说明：</p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await teacherService.downloadImportTemplate();
                      } catch (e: any) {
                        setError(e?.response?.data?.message || '下载模板失败');
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 text-xs font-medium"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>download</span>
                    下载模板
                  </button>
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>必填列：身份证号、姓名</li>
                  <li>可选列：工号、部门、职位、电话</li>
                  <li>校管理员列填 "是/否/Y/N"，留空表示不是</li>
                  <li>班级列填多个 classId 用 "/" "," ";" 分隔（如 2024-0001-0001;2024-0001-0002）</li>
                  <li>身份证已存在的老师会被跳过（不会覆盖）</li>
                  <li>初始密码 = 身份证号后 6 位</li>
                </ul>
              </div>

              <div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-on-surface
                    file:mr-4 file:py-2 file:px-4 file:rounded-xl
                    file:border-0 file:text-sm file:font-medium
                    file:bg-primary-50 file:text-primary-700
                    hover:file:bg-primary-100"
                />
                {file && (
                  <p className="text-sm text-on-surface-variant mt-2 font-label">
                    已选择：{file.name}
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <Stat label="总行数" value={result.totalRows} color="slate" />
                <Stat label="成功" value={result.success} color="success" />
                <Stat label="跳过" value={result.skipped} color="warning" />
                <Stat label="失败" value={result.failed} color="danger" />
              </div>
              <div className="border border-outline-variant rounded-xl overflow-hidden max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-container-low text-on-surface-variant sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">行</th>
                      <th className="text-left px-3 py-2 font-medium">身份证</th>
                      <th className="text-left px-3 py-2 font-medium">姓名</th>
                      <th className="text-left px-3 py-2 font-medium">状态</th>
                      <th className="text-left px-3 py-2 font-medium">说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.details.map((d, i) => (
                      <tr key={i} className="border-t border-outline-variant">
                        <td className="px-3 py-2 text-on-surface-variant">{d.rowNumber}</td>
                        <td className="px-3 py-2 font-mono text-xs text-on-surface-variant">{d.idCardNumber || '—'}</td>
                        <td className="px-3 py-2 text-on-surface">{d.name || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={
                            d.status === 'SUCCESS' ? 'text-success-700' :
                            d.status === 'SKIPPED' ? 'text-warning-700' : 'text-danger-700'
                          }>
                            {d.status === 'SUCCESS' ? '成功' : d.status === 'SKIPPED' ? '跳过' : '失败'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-on-surface-variant">{d.errorMessage || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-outline-variant flex items-center justify-end gap-2">
          {!result ? (
            <>
              <button onClick={handleClose}
                      className="h-10 px-5 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low transition">
                取消
              </button>
              <button onClick={handleSubmit}
                      disabled={importing || !file}
                      className="h-10 px-5 gradient-primary text-on-primary rounded-xl shadow-teal disabled:opacity-50 disabled:cursor-not-allowed transition font-medium">
                {importing ? '导入中…' : '开始导入'}
              </button>
            </>
          ) : (
            <button onClick={handleClose}
                    className="h-10 px-5 gradient-primary text-on-primary rounded-xl shadow-teal transition font-medium">
              关闭
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; color: 'slate' | 'success' | 'warning' | 'danger' }> = ({ label, value, color }) => {
  const cls = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    success: 'bg-success-50 border-success-200 text-success-700',
    warning: 'bg-warning-50 border-warning-200 text-warning-700',
    danger: 'bg-danger-50 border-danger-200 text-danger-700',
  }[color];
  return (
    <div className={`${cls} border rounded-xl p-3 text-center`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs mt-1">{label}</div>
    </div>
  );
};

export default TeacherImportDialog;
